<template>
  <div>
    <v-row>
      <v-col md="2">
        <app-select
          :items="dateFilterOptions"
          v-model="searchParams.date_filter_type"
          solo
          flat
          :dense="false"
          background-color="grey lighten-4"
          @input="handleDateFilterChange"
        />
      </v-col>
      <v-col md="3">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.scheduled_date_start"
          :label="
            searchParams.date_filter_type === 'delivered'
              ? 'Data de Entrega Inicial'
              : 'Data Programada Inicial'
          "
          @change="select(1)"
        />
      </v-col>
      <v-col md="3">
        <v-text-field
          hide-details="auto"
          type="date"
          solo
          flat
          background-color="grey lighten-4"
          v-model="searchParams.scheduled_date_end"
          :label="
            searchParams.date_filter_type === 'delivered'
              ? 'Data de Entrega Final'
              : 'Data Programada Final'
          "
          @change="select(1)"
        />
      </v-col>
      <v-col md="4">
        <company-select
          chips
          multiple
          v-model="searchParams.company_id"
          :item-value="(val) => val.id"
          :itemText="(val) => val.short_name"
          solo
          flat
          background-color="grey lighten-4"
          :dense="false"
          @change="select(1)"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col md="3">
        <app-text-search-field
          label="Pesquisar por produtos"
          @input="handleSearchInput($event)"
          :loading="isLoading"
        />
      </v-col>

      <v-col md="3">
        <app-select
          :items="deliveryTypeData"
          v-model="searchParams.delivery_type"
          @input="handleDeliveryTypeChange"
          solo
          flat
          :dense="false"
          background-color="grey lighten-4"
        />
      </v-col>

      <v-col md="6">
        <v-chip-group
          v-model="searchParams.delivery_status"
          multiple
          mandatory
          @change="handleDeliveryStatusChange"
        >
          <v-chip filter outlined value="pending"> Pendente </v-chip>
          <v-chip filter outlined value="processing"> Processando </v-chip>
          <v-chip filter outlined value="in_route"> Em Rota </v-chip>
          <v-chip filter outlined value="delivered"> Entregue </v-chip>
          <v-chip filter outlined value="fail"> Falha </v-chip>
          <v-chip filter outlined value="canceled"> Cancelado </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="3">
        <BrandsSearch
          @input="handleBrandInput($event)"
          solo
          flat
          background-color="grey lighten-4"
          :dense="false"
        /> </v-col
    ></v-row>

    <v-row class="mt-6">
      <v-col>
        <v-card flat color="grey lighten-4">
          <v-card-title class="d-flex justify-space-between mb-4 pt-5">
            <span class="text-subtitle-1">
              Relatório de entregas agrupadas por produto</span
            >

            <div class="d-flex gap-4">
              <v-chip-group
                class="mr-8"
                v-model="searchParams.orderBy"
                mandatory
                @change="select(1)"
              >
                <v-chip filter outlined value="product_code"> Código </v-chip>
                <v-chip filter outlined value="product_name">
                  Descrição
                </v-chip>
                <v-chip filter outlined value="quantidade"> Quantidade </v-chip>
                <v-chip filter outlined value="peso_total"> Peso Total </v-chip>
              </v-chip-group>

              <v-chip-group
                class="mr-8"
                v-model="searchParams.orderDirection"
                mandatory
                @change="select(1)"
              >
                <v-chip filter outlined value="asc"> A-Z </v-chip>
                <v-chip filter outlined value="desc"> Z-A </v-chip>
              </v-chip-group>

              <v-btn color="primary" size="large" class="mr-2">
                Total: {{ $format.decimal(deliveries.total_quantity || 0) }} un
                | {{ $format.decimal(deliveries.total_weight || 0) }} kg
              </v-btn>
              <v-btn color="success" size="large" @click="handleExport">
                Exportar para Excel
              </v-btn>
            </div>
          </v-card-title>
          <v-card-text>
            <v-data-table
              class="grey lighten-4"
              :headers="headers"
              :items="deliveries.data"
              hide-default-footer
              :items-per-page="-1"
              disable-sort
            >
              <template v-slot:item.sale_status="{ item }">
                {{ saleStatusMap[item.sale_status] || item.sale_status }}
              </template>

              <template v-slot:item.quantidade="{ item }">
                {{ Number(item.quantidade).toFixed(0) }}
              </template>

              <template v-slot:item.weight_gross="{ item }">
                {{ $format.decimal(item.weight_gross || 0) }}
              </template>

              <template v-slot:item.peso_total="{ item }">
                {{ $format.decimal(item.peso_total || 0) }}
              </template>
            </v-data-table>

            <app-pagination @click="select($event)" :data="deliveries" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AccountPlanSelect from "@/components/account-plan/ui/AccountPlanSelect.vue";
import CompanySelect from "@/components/company/ui/CompanySelect.vue";
import OperationSelect from "@/components/operation/ui/OperationSelect.vue";
import SalesmanSearch from "@/components/employees/ui/SalesmanSearch.vue";
import SaleDeliveryStatus from "@/components/sales/sections/SaleDeliveryStatus.vue";
import BrandsSearch from "@/components/brands/ui/BrandsSearch.vue";

import { format, startOfMonth } from "date-fns";

export default {
  components: {
    AccountPlanSelect,
    CompanySelect,
    SaleDeliveryStatus,
    SalesmanSearch,
    OperationSelect,
    BrandsSearch,
  },

  data() {
    return {
      headers: [
        { text: "Código", value: "product_code", align: "center" },
        { text: "Descrição", value: "product_name", align: "start" },
        { text: "Marca", value: "brand_name", align: "start" },
        { text: "Peso Bruto (kg)", value: "weight_gross", align: "center" },
        { text: "Quantidade", value: "quantidade", align: "center" },
        { text: "Peso Total (kg)", value: "peso_total", align: "center" },
      ],

      deliveryStatusMap: {
        pending: "Pendente",
        processing: "Processando",
        in_route: "Em Rota",
        delivered: "Entregue",
        fail: "Falha",
        canceled: "Cancelado",
      },

      deliveryTypeData: [
        { value: null, text: "Todos" },
        { value: "pickup", text: "Retirada" },
        { value: "delivery", text: "Entrega" },
      ],

      dateFilterOptions: [
        { value: "scheduled", text: "Data de Criação" },
        { value: "delivered", text: "Data de Entrega" },
      ],

      deliveries: { data: [] },

      searchParams: {
        orderBy: "quantidade",
        orderDirection: "desc",
        company_id: [],
        scheduled_date_start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        scheduled_date_end: format(new Date(), "yyyy-MM-dd"),
        date_filter_type: "scheduled",
        delivery_status: [
          "pending",
          "processing",
          "in_route",
          "delivered",
          "fail",
          "canceled",
        ],

        operation_id: null,
        delivery_type: null,
        text: null,
        brand_id: null,
      },

      isLoading: false,
    };
  },

  mounted() {
    this.select(1);
  },

  methods: {
    async select(page = 1) {
      this.searchParams.page = page;
      this.isLoading = true;

      try {
        const response = await this.$http.index(
          "sale/delivery-by-product-report",
          this.searchParams
        );
        this.deliveries = response;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleExport() {
      this.$http
        .download("sale/delivery-by-product-export", this.searchParams)
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Entregas_${this.searchParams.scheduled_date_start}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          this.$loading.finish();
        })
        .catch((error) => {
          console.log(error);
          this.$loading.finish();
        });
    },

    handleSearchInput(text) {
      this.searchParams.text = text ? text : null;
      this.select(1);
    },

    handleClearFilter() {
      this.searchParams.delivery_type = null;
      this.searchParams.brand_id = null;
      this.select(1);
    },

    handleDateFilterChange() {
      if (this.searchParams.date_filter_type === "delivered") {
        // Quando filtrar por data de entrega, setar status apenas para entregue
        this.searchParams.delivery_status = ["delivered"];
      } else {
        // Quando voltar para data programada, restaurar todos os status
        this.searchParams.delivery_status = [
          "pending",
          "processing",
          "in_route",
          "delivered",
          "fail",
          "canceled",
        ];
      }
      this.select(1);
    },

    handleDeliveryStatusChange() {
      // Se mudar o status manualmente, voltar para filtro por data programada
      if (this.searchParams.date_filter_type === "delivered") {
        this.searchParams.date_filter_type = "scheduled";
      }
      this.select(1);
    },

    handleDeliveryTypeChange() {
      // Se for tipo retirada, voltar para filtro por data de criação
      if (this.searchParams.delivery_type === "pickup") {
        this.searchParams.date_filter_type = "scheduled";
        // Restaurar todos os status quando voltar para data programada
        this.searchParams.delivery_status = [
          "pending",
          "processing",
          "in_route",
          "delivered",
          "fail",
          "canceled",
        ];
      }
      this.select(1);
    },

    handleBrandInput(brand) {
      this.searchParams.brand_id = brand ? brand.id : null;
      this.select(1);
    },
  },
};
</script>
